import { Field, Form, withFormik } from 'formik';
import { useEffect, useState, useRef } from 'preact/hooks';
import eCardMessageDetails from '../ecardsPreact.classes/eCardMessageDetails';
import { PreviewState } from '../ecardsPreact.constants/previewState';
import { Steps } from '../ecardsPreact.constants/steps';
import { EcardTypes } from '../ecardsPreact.constants/ecardTypes';
import ECardPreview from './ECardPreview';
import { ECardGlobalState } from './ECardGlobalState';
import ECardStepPersonalise from './ECardStepPersonalise';
import ECardStepControls from './ECardStepControls';
import ECardAddRecipientsOptions from './ECardAddRecipientsOptions';
import ECardAddRecipientsManual from './ECardAddRecipientsManual';
import ECardAddRecipientsCsv from './ECardAddRecipientsCsv';
import ECardStepDelivery from './ECardStepDelivery';
import ECardStepDonationSelect from './ECardStepDonationSelect';
import { parseJSON, lightFormat } from 'date-fns';
import requestManager from '../ecardsPreact.client/requestManager';
import { GADatalayerEcardCheckoutSteps } from '../ecardsPreact.constants/GADatalayerEcardCheckoutSteps';

/**
 * The ECardsApp functional component is wrapped by the App component which uses formik to handle the various form field states, as well as validation
 * @param {*} props
 * @returns
 */
export const ECardsApp = (props) => {
    const { values, touched, errors, isSubmitting, handleChange, handleBlur, handleSubmit } = props; // Specific formik props
    const {
        EcardDesignUrl,
        EcardDesignAlt,
        EcardDesignAspectRatio,
        PreviewBackgroundColor,
        FormBackgroundColor,
        MaxRecipients,
        DonationOptions,
        DonationBands,
        CurrentStep,
        BackUrl,
        EcardType,
        IconUrl,
    } = props;

    const recipients = values.EcardMessageDetails.Recipients;
    const isScheduledInitial =
        values.EcardMessageDetails.DateCreated === null ? null : values.EcardMessageDetails.DateCreated !== values.EcardMessageDetails.DateScheduled;

    const [selectedRecipient, setSelectedRecipient] = useState(0); // selected recipient index
    const [previewState, setPreviewState] = useState(values.PreviewState || PreviewState.Front);
    const [currentStep, setCurrentStep] = useState(CurrentStep || Steps.Personalise);
    const [isScheduled, setIsScheduled] = useState(isScheduledInitial);
    const [isValidCsv, setIsValidCsv] = useState(false);
    const [recipientsCsvError, setRecipientsCsvError] = useState();
    const [maxRecipientsVisible, setMaxRecipientsVisible] = useState(3);
    const envelope = useRef(null);
    const EcardCategory = props.GADatalayerEcardCheckout.EcardCategory;
    const EcardDesignName = props.GADatalayerEcardCheckout.EcardDesignName;
    const GtmDonationAmount = values.EcardMessageDetails.DonationAmount;

    const globalStore = {
        selectedRecipient: { get: selectedRecipient, set: setSelectedRecipient },
        currentStep: { get: currentStep, set: setCurrentStep },
        isScheduled: { get: isScheduled, set: setIsScheduled },
        isValidCsv: { get: isValidCsv, set: setIsValidCsv },
        recipientsCsvError: { get: recipientsCsvError, set: setRecipientsCsvError },
        maxRecipientsVisible: { get: maxRecipientsVisible, set: setMaxRecipientsVisible },
        isGtmCard: EcardType === EcardTypes.GiftThatMatters,
        ecardCategory: EcardCategory,
        ecardDesignName: EcardDesignName
    };

    const imageUploadNotLoaded = values.EcardMessageDetails.ImageUploadId && !values.EcardMessageDetails.ImageUploadUrl;

    useEffect(() => {
        // Set values.EcardMessageDetails.DateScheduled as JSON string value based on values.DeliveryDate and values.DeliveryTime
        if (values.DeliveryDate && values.DeliveryTime) {
            const date = parseJSON(values.DeliveryDate);
            const deliveryTime = values.DeliveryTime;
            const deliveryTimeSplit = deliveryTime.split(':');
            const deliveryTimeHours = parseInt(deliveryTimeSplit[0], 10);
            const deliveryTimeMinutes = parseInt(deliveryTimeSplit[1], 10);
            const dateTime = new Date(date.getFullYear(), date.getMonth(), date.getDate(), deliveryTimeHours, deliveryTimeMinutes);
            values.EcardMessageDetails.DateScheduled = dateTime.toJSON();
        }
    }, [values.DeliveryDate, values.DeliveryTime]);

    useEffect(() => {
        GADatalayerEcardCheckout(CurrentStep, EcardCategory, EcardType === EcardTypes.GiftThatMatters ? GtmDonationAmount.toString() : EcardDesignName);
    }, []);

    return (
        <ECardGlobalState.Provider value={globalStore}>
            <div class="ecards-app">
                <div className="ecards-app__preview" style={{ backgroundColor: PreviewBackgroundColor }}>
                    <div class="ecards-app__preview-content container">
                        <ecard-envelope ref={envelope} class="ecard-envelope ecard-envelope--no-hover">
                            <div class="ecard-envelope__wrap">
                                <div class="ecard-envelope__flap-front"></div>
                                <div class="ecard-envelope__flap-back"></div>
                                <div class="ecard-envelope__front"></div>
                                <div class="ecard-envelope__middle"></div>
                                <div class="ecard-envelope__content">
                                    <ECardPreview
                                        {...values.EcardMessageDetails}
                                        ecardDesignUrl={EcardDesignUrl}
                                        ecardDesignAlt={EcardDesignAlt}
                                        ecardDesignAspectRatio={EcardDesignAspectRatio}
                                        previewState={previewState}
                                        recipient={recipients[selectedRecipient]}
                                        onPreviewStateUpdate={(newState) => {
                                            setPreviewState(newState);
                                        }}
                                        envelope={envelope}
                                    />
                                    {typeof IconUrl !== 'undefined' && IconUrl !== '' && (
                                        <canvas
                                            class="ecard-envelope__confetti ecard-envelope__confetti--inside"
                                            is="confetti-canvas"
                                            data-confetti-canvas-src={IconUrl}
                                        ></canvas>
                                    )}
                                </div>
                            </div>
                            {typeof IconUrl !== 'undefined' && IconUrl !== '' && (
                                <canvas
                                    class="ecard-envelope__confetti ecard-envelope__confetti--outside"
                                    is="confetti-canvas"
                                    data-confetti-canvas-src={IconUrl}
                                ></canvas>
                            )}
                        </ecard-envelope>
                    </div>
                </div>
                <Form
                    class="ecards-app__form ecard-form"
                    style={{ backgroundColor: FormBackgroundColor }}
                    noValidate
                    disabled={values.FormSubmitted || isSubmitting}
                >
                    <ECardStepPersonalise
                        hidden={currentStep !== Steps.Personalise}
                        handleChange={handleChange}
                        previewState={previewState}
                        setPreviewState={setPreviewState}
                        recipients={recipients}
                        maxRecipients={MaxRecipients}
                        imageUploadNotLoaded={imageUploadNotLoaded}
                    />

                    {!globalStore.isGtmCard && (
                        <>
                            <ECardAddRecipientsOptions hidden={currentStep !== Steps.AddRecipientsOptions} />
                            <ECardAddRecipientsManual
                                hidden={currentStep !== Steps.AddRecipientsManual}
                                currentStep={currentStep}
                                recipientsCount={recipients.length ?? 0}
                                maxRecipients={MaxRecipients}
                            />
                            <ECardAddRecipientsCsv hidden={currentStep !== Steps.AddRecipientsCsv} currentStep={currentStep} maxRecipients={MaxRecipients} />
                        </>
                    )}

                    <ECardStepDelivery hidden={currentStep !== Steps.Delivery} />

                    {!globalStore.isGtmCard && (
                        <ECardStepDonationSelect
                            hidden={currentStep !== Steps.DonationSelect}
                            donationOptions={DonationOptions}
                            donationBands={DonationBands}
                            recipients={recipients}
                        />
                    )}

                    <ECardStepControls
                        errors={errors}
                        isSubmitting={values.FormSubmitted || isSubmitting}
                        touched={touched}
                        backUrl={BackUrl}
                        class="ecard-form__controls"
                    />
                </Form>
            </div>
        </ECardGlobalState.Provider>
    );
};

export const App = withFormik({
    displayName: 'eCard Creation Form',
    mapPropsToValues: (props) => {
        const otherDonationAmount = setInitialOtherAmount(props.EcardMessageDetails.DonationAmount, props.DonationOptions);

        return {
            PreviewState: PreviewState.Front,
            DeliveryDate: parseDeliveryDate(props.EcardMessageDetails.DateScheduled),
            DeliveryTime: parseDeliveryTime(props.EcardMessageDetails.DateScheduled),
            OtherDonationAmount: otherDonationAmount,
            IsOtherAmount: otherDonationAmount !== '',
            EcardMessageDetails: new eCardMessageDetails(props.EcardMessageDetails),
            ControlsMessage: '',
            SkipDonation: undefined,
            FormSubmitted: false,
            RecaptchaToken: undefined,
        };
    },
    // Custom sync validation. This is where the field validation is configured.
    validate: (values) => {
        const errors = {};

        const validateSenderName = _validateField(values.EcardMessageDetails.FromName, 'Your name');
        const validateDeliveryDate = _validateField(values.DeliveryDate, 'Delivery date');
        const validateDeliveryTime = _validateField(values.DeliveryTime, 'Delivery time');
        const validateDonationAmount = _validateField(values.EcardMessageDetails.DonationAmount, 'Donation', 'amount', false);
        const validateOtherDonationAmount = _validateField(values.OtherDonationAmount, 'OtherDonationAmount', 'otheramount', false);
        const validateMessage = _validateField(values.EcardMessageDetails.Message, 'Message');

        values.EcardMessageDetails.Recipients.forEach((recipient, index) => {
            const validateRecipientName = _validateField(recipient.Name, 'Recipient name');
            const validateRecipientEmail = _validateField(
                recipient.Email,
                'Recipient email',
                'email',
                true,
                'The address should be in a format similar to example@mariecurie.org.uk',
                values.EcardMessageDetails.Recipients
            );

            if (validateRecipientName || validateRecipientEmail) {
                if (!errors.EcardMessageDetails) {
                    errors.EcardMessageDetails = {};
                }

                if (!errors.EcardMessageDetails.Recipients) {
                    errors.EcardMessageDetails.Recipients = [];
                }

                if (errors.EcardMessageDetails.Recipients[index] === undefined) {
                    errors.EcardMessageDetails.Recipients[index] = {};
                }

                if (validateRecipientName) {
                    errors.EcardMessageDetails.Recipients[index].Name = validateRecipientName;
                }
                if (validateRecipientEmail) {
                    errors.EcardMessageDetails.Recipients[index].Email = validateRecipientEmail;
                }
            }
        });

        if (validateSenderName) {
            if (!errors.EcardMessageDetails) {
                errors.EcardMessageDetails = {};
            }
            errors.EcardMessageDetails.FromName = validateSenderName;
        }
        if (validateMessage) {
            if (!errors.EcardMessageDetails) {
                errors.EcardMessageDetails = {};
            }
            errors.EcardMessageDetails.Message = validateMessage;
        }

        if (validateDeliveryDate) {
            errors.DeliveryDate = validateDeliveryDate;
        }
        if (validateDeliveryTime) {
            errors.DeliveryTime = validateDeliveryTime;
        }

        if (values.SkipDonation !== true) {
            if (validateDonationAmount) {
                if (!errors.EcardMessageDetails) {
                    errors.EcardMessageDetails = {};
                }

                errors.EcardMessageDetails.DonationAmount = validateDonationAmount;
            }

            if (values.IsOtherAmount === true && validateOtherDonationAmount) {
                errors.OtherDonationAmount = validateOtherDonationAmount;
            }
        }

        return errors;
    },

    handleSubmit: (values, { setSubmitting, setFieldError, setFieldValue }) => {
        try {
            setSubmitting(true);

            grecaptcha.ready(async () => {
                var token = await grecaptcha.execute('6LeZGbMaAAAAANO9dtVMb0UUOiczJYagWKVeb8y6', { action: 'submit' });

                var ecardMessageDetails = values.EcardMessageDetails;

                ecardMessageDetails.RecaptchaToken = token;

                try {
                    const saveResponse = await requestManager.DoRequest('POST', '/api/EcardCreationApi/saveecard/', undefined, ecardMessageDetails);

                    if (saveResponse.Success === true) {
                        const redirectUrl = ecardMessageDetails.DonationAmount > 0 ? saveResponse.DonationUrl : saveResponse.DetailsUrl;

                        if (ecardMessageDetails.ImageToUpload) {
                            var token2 = await grecaptcha.execute('6LeZGbMaAAAAANO9dtVMb0UUOiczJYagWKVeb8y6', { action: 'submit' });
                            ecardMessageDetails.RecaptchaToken = token2;

                            // In order to upload the image, we have to sent the data using the FormData object.
                            let formData = new FormData();
                            formData.append('fileInput', ecardMessageDetails.ImageToUpload);

                            const imageUploadResponse = await requestManager.DoFormDataRequest(
                                'PUT',
                                `/api/EcardCreationApi/uploadimage/?ecardGuid=${saveResponse.EcardGuid}&recaptchaToken=${ecardMessageDetails.RecaptchaToken}`,
                                undefined,
                                formData
                            );

                            if (imageUploadResponse.Success !== true) {
                                throw new Error(imageUploadResponse.Message);
                            }
                        }

                        setFieldValue('FormSubmitted', true);

                        window.location.href = `${redirectUrl}?ecardGuid=${saveResponse.EcardGuid}`;
                    } else {
                        throw new Error(saveResponse.Message);
                    }
                } catch (error) {
                    setFieldError('ControlsMessage', `Sorry, there has been an error: ${error.message}. If you keep having this issue, please get in touch.`);
                    setSubmitting(false);
                }
            });
        } catch (error) {
            setFieldError('ControlsMessage', `Sorry, there has been an error: ${error.message}. If you keep having this issue, please get in touch.`);
            setSubmitting(false);
        }
    },
    displayName: 'ECardsCreationForm',
})(ECardsApp);

// TODO: Refactor into own file
function _validateField(value, label, type, required = true, hint = '', recipients = []) {
    let error;

    if (required && (!value || value.length === 0)) {
        error = `${label} cannot be blank.`;
    } else {
        if (type) {
            switch (type) {
                case 'email':
                    if (
                        !/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i.test(
                            value
                        )
                    ) {
                        error = 'Invalid email address.';
                    }
                    break;

                case 'tel':
                    // https://regexr.com/3c53v
                    if (!/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/.test(value)) {
                        error = `"${value}" is not a valid phone number.`;
                    }
                    break;

                case 'amount':
                    if (!/^\d+(\.\d{1,2})?$/.test(value) || value < 1) {
                        error = 'Please select an amount you would like to donate.';
                    }
                    break;

                case 'otheramount':
                    if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                        error = 'Please tell us how much you want to donate. The amount must be a number.';
                    } else if (value < 1) {
                        error = 'Sorry, £1 is the lowest single donation option due to admin costs.';
                    }
                    break;
            }
        }

        if (label === 'Message') {
            if (value.length > 240) {
                error = `${label} cannot be over 240 characters.`;
            }

            const messageRegex = new RegExp('([a-zA-Z0-9]+://)?([a-zA-Z0-9_]+:[a-zA-Z0-9_]+@)?([a-zA-Z0-9.-]+\\.[A-Za-z]{2,4})(:[0-9]+)?(/.*)?');

            if (messageRegex.test(value)) {
                error = `${label} contains some invalid data, such as an email address or a URL. Please review your message and try again.`;
            }
        }

        if (label === 'Recipient email' && recipients.length > 1) {
            if (recipients.filter((recipient) => recipient.Email === value).length > 1) {
                error = 'A recipient with this email address already exists. Recipient email addresses must be unique.';

                return error;
            }
        }
    }

    if (!error) {
        return;
    }

    return `${error}${hint !== '' ? ` ${hint}` : ''}`;
}

export function parseDeliveryDate(dateString) {
    const dateVal = parseJSON(dateString);
    return dateVal instanceof Date && !isNaN(dateVal) ? dateVal.toJSON() : undefined;
}

function parseDeliveryTime(dateString) {
    let deliveryTime = undefined;
    const dateVal = parseJSON(dateString);
    if (dateVal instanceof Date && !isNaN(dateVal) && dateVal.getMinutes() % 30 === 0) {
        deliveryTime = lightFormat(dateVal, 'HH:mm');
    }
    return deliveryTime;
}

function setInitialOtherAmount(amount, donationOptions) {
    if (!isNaN(amount) && amount >= 1 && donationOptions !== null && donationOptions.findIndex((x) => x.Amount === amount) === -1) {
        return `${amount}`;
    } else {
        return '';
    }
}

export function GADatalayerEcardCheckout(stepNumber, ecardCategory, ecardDesignName) {
    var stepDetails = GADatalayerEcardCheckoutSteps.find(obj => obj.stepNumber === stepNumber);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        'event': 'ecard_checkout',
        'category': ecardCategory,
        'design': ecardDesignName,
        'checkout_step_name': stepDetails.stepName,
        'checkout_step': stepDetails.displayStepNumber
    });
}

function padZero(value) {
    return value.toString().padStart(2, '0');
}